/**
 * ANANT INNER SELF GUIDANCE — Admin Panel
 * Route: /admin/anant-tarot
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, BarChart3, Settings as SettingsIcon, BookOpen, IndianRupee, RefreshCw, Save } from 'lucide-react';
import api from '../../utils/api';

const AnantTarotAdmin = () => {
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [readings, setReadings] = useState([]);
  const [cards, setCards] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, st, r, c, l] = await Promise.all([
        api.get('/admin/anant-tarot/stats'),
        api.get('/admin/anant-tarot/settings'),
        api.get('/admin/anant-tarot/readings?limit=50'),
        api.get('/admin/anant-tarot/cards'),
        api.get('/admin/anant-tarot/admin-ledger?limit=50'),
      ]);
      setStats(s.data);
      setSettings(st.data);
      setReadings(r.data.readings || []);
      setCards(c.data.cards || []);
      setLedger(l.data.entries || []);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  const seedCards = async () => {
    try {
      const r = await api.post('/admin/anant-tarot/seed-cards');
      toast.success(`Seeded ${r.data.inserted} new cards. Total: ${r.data.total}`);
      loadAll();
    } catch (e) { toast.error('Seed failed'); }
  };

  if (loading) return <div className="min-h-screen pt-28 flex items-center justify-center"><div className="animate-spin w-10 h-10 border-4 border-fuchsia-500 border-t-transparent rounded-full" /></div>;

  const TABS = [
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'cards', label: 'Cards (79)', icon: Sparkles },
    { id: 'readings', label: 'User Readings', icon: BookOpen },
    { id: 'ledger', label: 'Admin Ledger', icon: IndianRupee },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen pt-28 pb-12 px-4 bg-gradient-to-br from-violet-950 via-fuchsia-950 to-indigo-950 text-white" data-testid="tarot-admin-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-fuchsia-300 via-violet-300 to-amber-300 bg-clip-text text-transparent">
            Anant Inner Self — Admin
          </h1>
          <p className="text-fuchsia-200/60 text-sm mt-1">Manage Osho Zen Tarot cards, readings, settings, Affiliate Network ledger.</p>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 sticky top-28 bg-violet-950/80 backdrop-blur z-30">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tarot-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
                  tab === t.id ? 'bg-fuchsia-500 text-white' : 'bg-white/5 hover:bg-white/10'
                }`}
              ><Icon className="w-4 h-4" /> {t.label}</button>
            );
          })}
          <button onClick={loadAll} className="ml-auto px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>

        {tab === 'stats' && (
          <StatsView stats={stats} onSeed={seedCards} cardsCount={cards.length} />
        )}
        {tab === 'cards' && (
          <CardsView cards={cards} onReload={loadAll} />
        )}
        {tab === 'readings' && (
          <ReadingsView readings={readings} />
        )}
        {tab === 'ledger' && (
          <LedgerView ledger={ledger} />
        )}
        {tab === 'settings' && (
          <SettingsView settings={settings} reload={loadAll} />
        )}
      </div>
    </div>
  );
};

const StatsView = ({ stats, onSeed, cardsCount }) => (
  <div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {[
        { label: 'Total Readings', value: stats?.total_readings ?? 0, color: 'from-fuchsia-500 to-violet-500' },
        { label: 'Free Draws', value: stats?.total_free_draws ?? 0, color: 'from-amber-500 to-orange-500' },
        { label: 'Admin Pool (AT)', value: (stats?.admin_ledger_total_at || 0).toFixed(2), color: 'from-emerald-500 to-teal-500' },
        { label: 'Last 24h Readings', value: stats?.readings_24h ?? 0, color: 'from-pink-500 to-rose-500' },
      ].map(c => (
        <div key={c.label} className={`bg-gradient-to-br ${c.color} rounded-2xl p-4 text-white`}>
          <div className="text-xs uppercase opacity-80">{c.label}</div>
          <div className="text-2xl font-extrabold">{c.value}</div>
        </div>
      ))}
    </div>
    {cardsCount < 79 && (
      <div className="bg-amber-500/20 border border-amber-400/40 rounded-xl p-4 flex items-center gap-3">
        <Sparkles className="w-6 h-6 text-amber-300" />
        <div className="flex-1">
          <div className="font-semibold text-amber-200">Cards not fully seeded ({cardsCount}/79)</div>
          <div className="text-xs text-amber-100/70">Click below to seed all 79 Osho Zen cards.</div>
        </div>
        <button onClick={onSeed} className="px-4 py-2 rounded-lg bg-amber-500 text-white font-bold" data-testid="admin-tarot-seed-btn">Seed Cards</button>
      </div>
    )}
  </div>
);

const CardsView = ({ cards, onReload }) => {
  const [editing, setEditing] = useState(null);
  const [imgUrl, setImgUrl] = useState('');
  const [meaning, setMeaning] = useState('');

  const beginEdit = (c) => { setEditing(c); setImgUrl(c.image_url || ''); setMeaning(c.basic_meaning || ''); };
  const cancelEdit = () => { setEditing(null); };
  const save = async () => {
    try {
      await api.put(`/admin/anant-tarot/cards/${editing.cardId}`, {
        cardId: editing.cardId, name: editing.name, suit: editing.suit,
        image_url: imgUrl, basic_meaning: meaning,
      });
      toast.success('Card updated');
      cancelEdit(); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
        {cards.map(c => (
          <button key={c.cardId} onClick={() => beginEdit(c)} data-testid={`admin-tarot-card-${c.cardId}`} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-fuchsia-400/50 transition">
            <div className="aspect-[2/3] bg-violet-900 overflow-hidden"><img src={c.image_url} alt={c.name} className="w-full h-full object-cover" /></div>
            <div className="p-1.5 text-center">
              <div className="text-[10px] text-fuchsia-300/60">{c.cardId}</div>
              <div className="text-xs font-bold text-white truncate">{c.name}</div>
              <div className="text-[9px] text-fuchsia-200/50">{c.suit}</div>
            </div>
          </button>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur p-3" onClick={cancelEdit}>
          <div onClick={e => e.stopPropagation()} className="bg-violet-950 border border-fuchsia-400/40 rounded-2xl max-w-lg w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg">Edit: {editing.name}</h3>
              <button onClick={cancelEdit} className="text-fuchsia-200">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-fuchsia-300/80 font-semibold mb-1 block">Image URL</label>
                <input value={imgUrl} onChange={e => setImgUrl(e.target.value)} className="w-full bg-violet-900 border border-fuchsia-400/30 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-fuchsia-300/80 font-semibold mb-1 block">Basic Meaning</label>
                <textarea value={meaning} onChange={e => setMeaning(e.target.value)} rows={3} className="w-full bg-violet-900 border border-fuchsia-400/30 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={cancelEdit} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm">Cancel</button>
                <button onClick={save} className="px-4 py-2 rounded-lg bg-fuchsia-500 text-white font-bold text-sm flex items-center gap-1"><Save className="w-4 h-4" /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReadingsView = ({ readings }) => (
  <div className="space-y-3">
    {readings.length === 0 && <div className="text-center py-12 text-fuchsia-200/60">No readings yet.</div>}
    {readings.map(r => (
      <div key={r.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-xs text-fuchsia-200/60">{new Date(r.created_at).toLocaleString()} · {r.user_username || r.user_email}</div>
          <div className="text-xs font-bold text-amber-300">{r.cost_at} AT · Admin: {r.admin_credit} AT</div>
        </div>
        <div className="text-white font-semibold italic">&ldquo;{r.question}&rdquo;</div>
        <div className="text-fuchsia-200/70 text-xs mt-2">Cards: {(r.cards || []).map(c => c.name).join(' · ')}</div>
      </div>
    ))}
  </div>
);

const LedgerView = ({ ledger }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-white/5"><tr className="text-left text-fuchsia-200"><th className="p-3">When</th><th className="p-3">User</th><th className="p-3 text-right">AT Credited</th></tr></thead>
      <tbody>
        {ledger.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-fuchsia-200/60">No entries yet.</td></tr>}
        {ledger.map(e => (
          <tr key={e.id} className="border-t border-white/5">
            <td className="p-3 text-xs">{new Date(e.created_at).toLocaleString()}</td>
            <td className="p-3 text-xs font-mono">{e.user_id?.slice(0, 8)}</td>
            <td className="p-3 text-right font-bold text-amber-300">{e.amount} AT</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SettingsView = ({ settings, reload }) => {
  const [form, setForm] = useState({
    spread_cost_at: settings?.spread_cost_at ?? 100,
    free_daily_enabled: settings?.free_daily_enabled ?? true,
    module_enabled: settings?.module_enabled ?? true,
    welcome_message: settings?.welcome_message ?? '',
    legal_disclaimer: settings?.legal_disclaimer ?? '',
    whatsapp_cta_url: settings?.whatsapp_cta_url ?? 'https://wa.me/919579369830?text=TAROT',
    whatsapp_cta_label: settings?.whatsapp_cta_label ?? 'Need personal guidance? Chat on WhatsApp',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/anant-tarot/settings', form);
      toast.success('Settings saved');
      reload();
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 max-w-2xl space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-fuchsia-300/80 font-semibold mb-1 block">Spread Cost (AT)</label>
          <input type="number" min="0" value={form.spread_cost_at} onChange={e => setForm(f => ({ ...f, spread_cost_at: parseFloat(e.target.value) || 0 }))} data-testid="admin-tarot-cost-input" className="w-full bg-violet-900 border border-fuchsia-400/30 rounded-lg px-3 py-2 text-sm text-white" />
        </div>
        <div className="flex flex-col gap-2 justify-center">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.module_enabled} onChange={e => setForm(f => ({ ...f, module_enabled: e.target.checked }))} /> Module Enabled</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.free_daily_enabled} onChange={e => setForm(f => ({ ...f, free_daily_enabled: e.target.checked }))} /> Daily Free Draw</label>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-fuchsia-300/80 font-semibold mb-1 block">Welcome Message</label>
        <input value={form.welcome_message} onChange={e => setForm(f => ({ ...f, welcome_message: e.target.value }))} className="w-full bg-violet-900 border border-fuchsia-400/30 rounded-lg px-3 py-2 text-sm text-white" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-fuchsia-300/80 font-semibold mb-1 block">Legal Disclaimer</label>
        <textarea rows={2} value={form.legal_disclaimer} onChange={e => setForm(f => ({ ...f, legal_disclaimer: e.target.value }))} className="w-full bg-violet-900 border border-fuchsia-400/30 rounded-lg px-3 py-2 text-sm text-white" />
      </div>
      <div className="border-t border-fuchsia-400/20 pt-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-green-300 font-bold">WhatsApp Support CTA (shown on Reading pages)</div>
        <div>
          <label className="text-xs uppercase tracking-wider text-fuchsia-300/80 font-semibold mb-1 block">WhatsApp URL</label>
          <input
            value={form.whatsapp_cta_url}
            onChange={e => setForm(f => ({ ...f, whatsapp_cta_url: e.target.value }))}
            placeholder="https://wa.me/919579369830?text=TAROT"
            data-testid="admin-tarot-wa-url"
            className="w-full bg-violet-900 border border-fuchsia-400/30 rounded-lg px-3 py-2 text-sm text-white font-mono"
          />
          <div className="text-[10px] text-fuchsia-300/60 mt-1">Format: https://wa.me/{'{'}phone{'}'}?text={'{'}prefill{'}'} · Prefix country code without &quot;+&quot; (e.g. 919579369830)</div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-fuchsia-300/80 font-semibold mb-1 block">Button Label</label>
          <input
            value={form.whatsapp_cta_label}
            onChange={e => setForm(f => ({ ...f, whatsapp_cta_label: e.target.value }))}
            placeholder="Need personal guidance? Chat on WhatsApp"
            data-testid="admin-tarot-wa-label"
            className="w-full bg-violet-900 border border-fuchsia-400/30 rounded-lg px-3 py-2 text-sm text-white"
          />
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-tarot-save-settings" className="px-5 py-2 rounded-xl bg-fuchsia-500 text-white font-bold flex items-center gap-1 disabled:opacity-50">
        <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );
};

export default AnantTarotAdmin;
